/* eslint-disable eqeqeq */

import { Space, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  JiraConfig,
  JiraEpic,
  MappingMechanism,
  SettingsMeta,
} from 'api/integrations/jira';
import { LinkButton } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { Select } from 'components/atoms/select';
import { Caption01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { BodyLong01 } from 'ozone/atoms';
import { useQueryJiraEpic } from 'queries/jira-integration';
import { FormItem, Title } from '../styles';
import { SectionKeys } from '../types';

const RadioText = styled(Space).attrs({ direction: 'vertical', size: 0 })`
  p {
    ${BodyLong01};
    color: var(--gray-100);
    margin-bottom: 0;
  }
  small {
    ${Caption01};
    color: var(--gray-80);
  }
`;

const ProjectNameContainer = styled(Space)`
  margin-top: var(--spacing-03);
`;

interface EpicMapping {
  jiraConfig: JiraConfig;
  onChange: (updatedObject: SettingsMeta, key?: SectionKeys) => void;
  onDirty: (status: boolean) => void;
  updating?: SectionKeys;
}

const epicTypeDependencies = [
  MappingMechanism.Fixed,
  MappingMechanism.Automatic,
  MappingMechanism.PromptDuringProjectcreation,
];

const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (child: JSX.Element) => JSX.Element;
  children: JSX.Element;
}) => (condition ? wrapper(children) : children);

export const EpicMapping: FC<EpicMapping> = ({
  jiraConfig,
  updating,
  onChange,
  onDirty,
}) => {
  const [form] = Form.useForm<SettingsMeta>();
  const [epicInputForm] = Form.useForm<JiraConfig>();

  const {
    settingsMeta: { fixedEpicIssueTypeId },
  } = jiraConfig;
  const [showEpicSelect, setShowEpicSelect] = useState(false);
  const [showEpicModal, setShowEpicModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const title = useTranslation('settings.integrations.jira.mapping.title');
  const mappingIssueHelp = useTranslation(
    'settings.integrations.jira.mapping.issue.help',
  );
  const mappingFixedTitle = useTranslation(
    'settings.integrations.jira.mapping.fixed.title',
  );
  const mappingIssueTitle = useTranslation(
    'settings.integrations.jira.mapping.issue.title',
  );
  const mappingProjectHelp = useTranslation(
    'settings.integrations.jira.mapping.project.help',
  );
  const mappingProjectTitle = useTranslation(
    'settings.integrations.jira.mapping.project.title',
  );
  const mappingAutomaticHelp = useTranslation(
    'settings.integrations.jira.mapping.automatic.help',
  );
  const mappingAutomaticTitle = useTranslation(
    'settings.integrations.jira.mapping.automatic.title',
  );
  const modalTitle = useTranslation(
    'settings.integrations.jira.mapping.modal.title',
  );
  const selectEpicTitle = useTranslation(
    'settings.integrations.jira.mapping.fixed.select_epic.title',
  );
  const selectEpicHelp = useTranslation(
    'settings.integrations.jira.mapping.fixed.select_epic.help',
  );
  const saveText = useTranslation('save');
  const invalidEpicText = useTranslation(
    'settings.integrations.jira.mapping.no_epic',
  );

  const mappingOptions = [
    {
      value: MappingMechanism.Fixed,
      title: mappingFixedTitle,
      help: '',
      default: true,
    },
    {
      value: MappingMechanism.PromptDuringProjectcreation,
      title: mappingProjectTitle,
      help: mappingProjectHelp,
    },
    {
      value: MappingMechanism.Automatic,
      title: mappingAutomaticTitle,
      help: mappingAutomaticHelp,
    },
    {
      value: MappingMechanism.PromptDuringIssuecreation,
      title: mappingIssueTitle,
      help: mappingIssueHelp,
    },
  ];

  /**
   * @description If the selected value is FIXED then enable the Select and
   * trigger the change callback to the parent on Select changed, else trigger the change callback to parent.
   * @param event
   */
  const onMappingSelect = (updatedObject: SettingsMeta) => {
    const { epicMappingMechanism: mapping } = updatedObject;

    if (mapping === MappingMechanism.Fixed) {
      setShowEpicSelect(true);
      onDirty(true);
    } else {
      onDirty(false);
      setShowEpicSelect(false);
      onChange(
        { ...jiraConfig.settingsMeta, ...updatedObject },
        SectionKeys.EPIC_MAPPING,
      );
    }
  };

  const validationMessage = useTranslation('input.required');

  const onEpicSelect = () => {
    setShowEpicModal(false);
    epicInputForm.validateFields().then(() => {
      const formData = epicInputForm.getFieldsValue(true);

      if (formData.fixedEpicId) {
        onDirty(false);

        formData.epicMappingMechanism = MappingMechanism.Fixed;
        formData.fixedEpicId = formData.fixedEpicId.value;

        onChange(
          { ...jiraConfig.settingsMeta, ...formData },
          SectionKeys.EPIC_MAPPING,
        );
      }
    });
  };

  const { data: epicList, isLoading: loadingEpicList } = useQueryJiraEpic(
    jiraConfig.integrationId,
    jiraConfig.settingsMeta.fixedProjectId,
    { enabled: !!jiraConfig.settingsMeta.fixedProjectId },
  );

  const [selectedIssueType, setSelectedIssueType] =
    useState<JiraEpic | undefined>(undefined);

  useEffect(() => {
    if (jiraConfig != null) {
      setSelectedIssueType(
        epicList?.find(
          (epic) => epic.id == jiraConfig.settingsMeta.fixedEpicId,
        ),
      );
      setShowEpicSelect(
        jiraConfig.settingsMeta.epicMappingMechanism === MappingMechanism.Fixed,
      );
    }
  }, [epicList, jiraConfig, setSelectedIssueType]);

  const formSetFieldsValueRef = useRef(form.setFieldsValue);

  useEffect(() => {
    formSetFieldsValueRef.current = form.setFieldsValue;
  }, [formSetFieldsValueRef, form]);

  useEffect(() => {
    formSetFieldsValueRef.current?.(jiraConfig.settingsMeta);
  }, [jiraConfig, formSetFieldsValueRef]);

  useEffect(() => {
    if (updating) {
      if (updating === SectionKeys.EPIC_MAPPING) {
        setLoading(true);
      } else {
        setDisabled(true);
      }
    } else {
      setLoading(false);
      setDisabled(false);
    }
  }, [updating, setLoading, setDisabled]);

  return (
    <>
      <Title>{title}</Title>
      <Form
        form={form}
        onValuesChange={(val) => onMappingSelect(val)}
        initialValues={jiraConfig.settingsMeta}
      >
        <FormItem name="epicMappingMechanism">
          <Radio.Group disabled={disabled ?? loading}>
            <Space direction="vertical" size="middle">
              {mappingOptions.map((mapOption) => (
                <ConditionalWrapper
                  key={mapOption.value}
                  condition={
                    fixedEpicIssueTypeId == null &&
                    epicTypeDependencies.includes(mapOption.value)
                  }
                  wrapper={(children) => (
                    <Tooltip title={invalidEpicText}>{children}</Tooltip>
                  )}
                >
                  <Radio
                    value={mapOption.value}
                    key={mapOption.value}
                    disabled={
                      fixedEpicIssueTypeId == null &&
                      epicTypeDependencies.includes(mapOption.value)
                    }
                  >
                    <RadioText>
                      <p>{mapOption.title}</p>
                      <small>{mapOption.help}</small>
                      {showEpicSelect &&
                      mapOption.value === MappingMechanism.Fixed ? (
                        <ProjectNameContainer>
                          <p>{selectEpicTitle}:</p>
                          <LinkButton onClick={() => setShowEpicModal(true)}>
                            {isEmpty(selectedIssueType)
                              ? selectEpicHelp
                              : selectedIssueType?.name}
                          </LinkButton>
                        </ProjectNameContainer>
                      ) : null}
                    </RadioText>
                  </Radio>
                </ConditionalWrapper>
              ))}
            </Space>
          </Radio.Group>
        </FormItem>
      </Form>
      <Modal
        visible={showEpicModal}
        title={modalTitle}
        confirmLoading={false}
        onCancel={() => setShowEpicModal(false)}
        onOk={onEpicSelect}
        okText={saveText}
      >
        <Form
          initialValues={{
            ...jiraConfig.settingsMeta,
            fixedEpicId: {
              label: selectedIssueType?.name,
              value: selectedIssueType?.id,
            },
          }}
          form={epicInputForm}
          preserve={false}
        >
          <FormItem
            name="fixedEpicId"
            rules={[{ required: true, message: validationMessage }]}
          >
            <Select
              options={(epicList || [])?.map((epic) => ({
                value: epic.id,
                label: epic.name,
              }))}
              isLoading={loadingEpicList}
            />
          </FormItem>
        </Form>
      </Modal>
    </>
  );
};
